"use client"

import { EVENTS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import {
  bulkDeleteEvents,
  bulkUpdateEventStatus,
  createEvent,
  createEventLocalization,
  deleteEvent,
  getEvents
} from "@/services/event-services"
import { useDomainStore } from "@/stores/use-domain-store"
import { EventLocalizationRequest } from "@/types/codegen/data-contracts"
import { CreateEventData, EventData, PaginationMeta } from "@/types/event"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { generateDuplicateName } from "@/utils/generate-duplicate-name"
import { App } from "antd"
import { t } from "i18next"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"


const useEventServices = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const enforcedDomainRef = useRef(enforcedDomain)
  const { modal, message } = App.useApp();

  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [events, setEvents] = useState<EventData[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [filterTabPage, setFilterTabPage] = useState("preview")
  const [needRefresh, setNeedRefresh] = useState(0)

  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [sortValues, setSortValues] = useState<{ [key: string]: string } | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  })

  const { hasPermission } = useCheckPermission()

  useEffect(() => {
    enforcedDomainRef.current = enforcedDomain
  }, [enforcedDomain])

  const fetchEventsData = useCallback(
    async (paramsFromUrl: Record<string, any> = {}) => {
      setLoading(true)
      try {
        const { publicationState, sort, ...rest } = paramsFromUrl
        const params = {
          page: String(pagination.page),
          pageSize: String(pagination.pageSize),
          publicationState: publicationState || filterTabPage,
          sort: sort || "createdAt:desc",
          ...rest,
          ...(enforcedDomainRef.current ? { domain: enforcedDomainRef.current } : {}),
        }
        const response = await getEvents(params)
        if (response && response.data) {
          setEvents(response.data)
          if (response.meta?.pagination) {
            setPagination(response.meta.pagination)
          }
        } else {
          setEvents([])
        }
      } catch (err) {
        message.error(t("Error when fetching event list"))
      } finally {
        setLoading(false)
      }
    },
    [pagination.page, pagination.pageSize, filterTabPage]
  )

  useEffect(() => {
    const parsedParams = queryString.parse(searchParams.toString())
    const newFilterValues: { [key: string]: string } = {}
    const newSortValues: { [key: string]: string } = {}

    const systemKeys = ["sort", "publicationState"]

    for (const key in parsedParams) {
      const value = parsedParams[key]
      if (typeof value === "string") {
        if (systemKeys.includes(key)) {
          newSortValues[key] = value
          if (key === "publicationState") setFilterTabPage(value)
        } else if (key !== "page" && key !== "pageSize") {
          newFilterValues[key] = value
        }
      }
    }

    if (enforcedDomain) {
      newFilterValues.domain = enforcedDomain
    }

    setFilterValues(newFilterValues)
    setSortValues(newSortValues)
  }, [searchParams, enforcedDomain])

  useEffect(() => {
    if (filterValues === null && sortValues === null) return
    const params = { ...filterValues, ...sortValues }
    fetchEventsData(params)
  }, [filterValues, sortValues, pagination.page, pagination.pageSize, fetchEventsData, needRefresh])

  const updateUrl = useCallback((newParams: Record<string, any>) => {
    const current = queryString.parse(searchParams.toString())
    const merged = { ...current, ...newParams, ...(enforcedDomain ? { domain: enforcedDomain } : {}) }
    const searchString = queryString.stringify(merged, { skipEmptyString: true, skipNull: true })
    router.push(`${pathname}?${searchString}`)
  }, [pathname, router, searchParams, enforcedDomain])

  const onSearch = useCallback(
    (filters: any) => {
      setPagination(prev => ({ ...prev, page: 1 }))
      const current = queryString.parse(searchParams.toString())
      const newQuery: any = { ...filters }
      if (enforcedDomain) {
        newQuery.domain = enforcedDomain
      }
      if (current.publicationState) newQuery.publicationState = current.publicationState
      if (current.sort) newQuery.sort = current.sort

      const searchString = queryString.stringify(newQuery, { skipEmptyString: true, skipNull: true })
      router.push(searchString ? `${pathname}?${searchString}` : pathname)
    },
    [pathname, router, searchParams, enforcedDomain],
  )

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    const current = queryString.parse(searchParams.toString())
    const newParams: any = {}
    if (current.sort) newParams.sort = current.sort
    if (current.publicationState) newParams.publicationState = current.publicationState

    const searchString = queryString.stringify(newParams)
    router.push(searchString ? `${pathname}?${searchString}` : pathname)
    setPagination(prev => ({ ...prev, page: 1 }))
    setNeedRefresh(prev => prev + 1)
  }

  const handleChangeTab = (key: string) => {
    setPagination(prev => ({ ...prev, page: 1 }))
    updateUrl({ publicationState: key })
  }

  const handleDelete = async (id: number, currentParams?: any) => {
    modal.confirm({
      title: t("Confirm delete"),
      content: t("Are you sure to delete this event?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await deleteEvent(id)
          message.success(t("Event deleted successfully"))
          setNeedRefresh(prev => prev + 1)
        } catch {
          message.error(t("Error when delete event"))
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleBulkDelete = async (ids: number[], currentParams?: any) => {
    modal.confirm({
      title: t("Confirm delete"),
      content: t("Are you sure to delete these event?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await bulkDeleteEvents(ids)
          setSelectedRowKeys([])
          message.success(t("Delete events successfully"))
          setNeedRefresh(prev => prev + 1)
        } catch {
          message.error(t("Error when delete events"))
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleDuplicate = async (id: number, currentParams?: any) => {
    const event = events.find(e => e.id === id)
    if (!event) return message.error(t("Event not found"))
    try {
      setLoading(true)
      const existingNames = events.map(item => item.attributes.name).filter(Boolean)
      const name = generateDuplicateName(event.attributes.name, existingNames)
      const coverId = event.attributes.cover?.data?.id
      if (!coverId) {
        message.error(t("Event must have cover image"))
        return
      }
      const payload: CreateEventData = {
        name,
        date: event.attributes.date,
        location: event.attributes.location,
        contact: event.attributes.contact,
        detail: event.attributes.detail,
        shortDescription: event.attributes.shortDescription,
        locale: event.attributes.locale,
        cover: coverId,
        websites: event.attributes.websites?.data?.map(w => w.id),
      }
      await createEvent(payload)
      message.success(t("Duplicate event successfully"))
      setNeedRefresh(prev => prev + 1)
    } catch {
      message.error(t("Error when duplicate event"))
    } finally {
      setLoading(false)
    }
  }

  const handleBulkUpdateStatus = async (ids: number[], status: "publish" | "draft", currentParams?: any) => {
    try {
      setLoading(true)
      await bulkUpdateEventStatus(ids, status)
      setSelectedRowKeys([])
      message.success(t("Update status successfully"))
      setNeedRefresh(prev => prev + 1)
    } catch {
      message.error(t("Error when update status"))
    } finally {
      setLoading(false)
    }
  }

  const handleLocaleAction = useCallback(
    (record: EventData, targetLocale: string) => {
      if (!targetLocale) return
      const baseLocale = record.attributes?.locale
      if (baseLocale === targetLocale) {
        router.push(`/admin${EVENTS.EDIT.PATH}?id=${record.id}`)
        return
      }
      const localization = record.attributes?.localizations?.data?.find(
        (item: any) => item.attributes?.locale === targetLocale,
      )
      if (localization) {
        router.push(`/admin${EVENTS.EDIT.PATH}?id=${localization.id}`)
        return
      }
      router.push(`/admin/events/translate?id=${record.id}&locale=${targetLocale}`)
    },
    [router],
  )

  const initialFilterValues = useMemo(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      if (!["page", "pageSize", "sort", "publicationState"].includes(key)) {
        params[key] = value
      }
    })
    return params
  }, [searchParams])

  const handleCreateEventLocalization = async (id: number, data: EventLocalizationRequest) => {
    setLoading(true);
    try {
      const res = await createEventLocalization(id, data);
      if (res) {

        return res;
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    events,
    loading,
    pagination,
    setPagination,
    filterTabPage,
    setFilterTabPage: handleChangeTab,
    selectedRowKeys,
    setSelectedRowKeys,
    filterRef,
    handleBulkUpdateStatus,
    handleLocaleAction,
    onSearch,
    onRefresh,
    handleDelete,
    handleBulkDelete,
    handleDuplicate,
    hasPermission,
    updateUrl,
    filterValues,
    sortValues,
    initialFilterValues,
    handleCreateEventLocalization
  }
}

export default useEventServices
